<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Badge, Button, Card } from '@drama/ui'

import { api, download, getToken, inGuild } from '../api.js'
import { INVITATION_BADGE, invitationState } from '../invitation.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const detail = ref(null)
const error = ref('')

const when = (/** @type {string} */ iso) => new Date(iso).toLocaleString()
const nameOf = (/** @type {string|null} */ id) => (id === null ? '—' : detail.value.agent_names[id] ?? id)
/** 质保期写成天或小时。 @param {number} seconds */
const span = (seconds) => (seconds % 86400 === 0 ? t('task.contract.days', { n: seconds / 86400 }) : t('task.contract.hours', { n: Math.round(seconds / 360) / 10 }))
const STAGE_BADGE = { paid: 'success', due: 'warning', forfeited: 'danger', transferred: 'info' }
const SEVERITY_BADGE = { blocker: 'danger', major: 'warning', minor: 'neutral' }

/** 竞标任务收到的标书：只有发布这个任务的 Agent 的主人看得到，其他人为 null。 */
const bids = ref(null)
const BID_BADGE = { submitted: 'info', awarded: 'success', declined: 'neutral' }

async function load() {
  detail.value = await api('GET', inGuild(`/api/voko/agents/${route.params.id}/tasks/${route.params.taskId}`))
  const mine = detail.value.contract !== null && detail.value.contract.mode === 'bid' && detail.value.task.publisher_agent_id === route.params.id
  bids.value = mine ? (await api('GET', inGuild(`/api/voko/agents/${route.params.id}/tasks/${route.params.taskId}/bids`))).bids : null
}

/** 竞标窗口什么时候结束：发布时间 + bid_window_sec。 */
const bidUntil = computed(() => (detail.value === null || detail.value.contract === null || detail.value.contract.mode !== 'bid' ? null : new Date(Date.parse(detail.value.task.created_at) + detail.value.contract.bid_window_sec * 1000)))

/**
 * 竞速里一份提交的验收结果：通过、驳回，或还在排队（最早一份没验的标为「下一个验」）。
 * @param {{ id: string }} submission
 * @returns {'accept'|'reject'|'next'|'waiting'}
 */
function raceState(submission) {
  const review = detail.value.reviews.find((item) => item.submission_id === submission.id)
  if (review !== undefined) return review.action === 'reject' ? 'reject' : 'accept'
  const pending = detail.value.submissions.filter((item) => !detail.value.reviews.some((review) => review.submission_id === item.id))
  return pending[0].id === submission.id ? 'next' : 'waiting'
}
const RACE_BADGE = { accept: 'success', reject: 'danger', next: 'warning', waiting: 'neutral' }

/** 公会负责人验收公司 Agent 自己做的 solo 任务。 @param {'accept'|'reject'} decision */
async function reviewSolo(decision) {
  error.value = ''
  try {
    await api('POST', `/api/voko/solo-reviews/${route.params.taskId}`, { decision })
    await load()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

/** @param {{ file_id: string, filename: string }} artifact */
async function save(artifact) {
  error.value = ''
  try {
    await download(inGuild(`/api/voko/agents/${route.params.id}/files/${artifact.file_id}`), artifact.filename)
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

/** @param {number} bytes */
const size = (bytes) => (bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`)

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  try {
    await load()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
})
</script>

<template>
  <p><router-link :to="`/agents/${route.params.id}/board`">{{ t('task.back') }}</router-link></p>
  <p v-if="error" role="alert">{{ error }}</p>
  <template v-if="detail">
    <h1>{{ detail.task.title }} <Badge>{{ t(`board.status.${detail.task.status}`) }}</Badge></h1>
    <p>{{ t(`board.kinds.${detail.task.kind}`) }}<template v-if="detail.task.size"> · {{ t(`task.sizes.${detail.task.size}`) }}</template> · {{ t(`board.templates.${detail.contract !== null && [`race`, `bid`].includes(detail.contract.mode) ? detail.contract.mode : detail.task.template}`) }} · {{ t('board.reward', { reward: detail.task.reward }) }} · {{ t('board.rarity', { rarity: detail.task.rarity }) }} · {{ detail.task.deadline === null ? t('board.no_deadline') : t('board.deadline', { deadline: when(detail.task.deadline) }) }}</p>
    <Card class="voko-section">
      <h2>{{ t('task.goal') }}</h2>
      <p>{{ detail.task.goal }}</p>
      <p class="voko-pre">{{ detail.task.spec }}</p>
      <template v-if="detail.task.background"><h2>{{ t('task.background') }}</h2><p class="voko-pre">{{ detail.task.background }}</p></template>
      <template v-if="detail.task.out_of_scope"><h2>{{ t('task.outOfScope') }}</h2><p class="voko-pre">{{ detail.task.out_of_scope }}</p></template>
      <template v-if="detail.task.skills.length > 0"><p>{{ t('task.skills') }}：{{ detail.task.skills.join('、') }}</p></template>
    </Card>
    <Card v-if="detail.contract" class="voko-section">
      <h2>{{ t('task.contract.title') }} <Badge>{{ t(`task.contract.modes.${detail.contract.mode}`) }}</Badge></h2>
      <p>{{ t(`task.contract.about.${detail.contract.mode}`) }}</p>
      <ul>
        <li v-if="detail.task.template === 'contest'">{{ t('task.contract.entrants', { n: detail.entry_count, max: detail.contract.max_entrants }) }}</li>
        <li v-if="bidUntil !== null">{{ t('task.contract.bidUntil', { at: bidUntil.toLocaleString() }) }} · {{ t(`task.contract.bidSelections.${detail.contract.bid_selection}`) }}</li>
        <li v-if="detail.contract.mode === 'project'">{{ t('task.contract.split', { share: detail.contract.acceptance_share, rest: 100 - detail.contract.acceptance_share, warranty: span(detail.contract.warranty_sec) }) }}</li>
        <li v-if="detail.contract.consolation > 0">{{ t('task.contract.consolation', { percent: detail.contract.consolation }) }}</li>
        <li v-if="detail.contract.acceptance_doc !== null">{{ t('task.contract.doc') }}：<code>{{ detail.contract.acceptance_doc }}</code></li>
        <li v-if="detail.contract.locked_at">{{ t('task.contract.locked', { at: when(detail.contract.locked_at) }) }}</li>
        <li v-if="detail.warranty_until">{{ t('task.contract.warrantyUntil', { at: when(detail.warranty_until) }) }}</li>
      </ul>
      <p v-if="detail.contract.mode === 'solo' && detail.task.status === 'submitted'">
        {{ t('task.contract.soloReview') }}
        <Button size="sm" @click="reviewSolo('accept')">{{ t('task.contract.accept') }}</Button>
        <Button variant="secondary" size="sm" @click="reviewSolo('reject')">{{ t('task.contract.reject') }}</Button>
      </p>
    </Card>
    <Card v-if="bids !== null" class="voko-section task-bids">
      <h2>{{ t('task.bids.title') }}</h2>
      <p>{{ t(`task.bids.how.${detail.contract.bid_selection}`) }}</p>
      <p v-if="bids.length === 0">{{ t('task.bids.none') }}</p>
      <table v-else class="voko-table">
        <thead><tr><th scope="col">{{ t('task.bids.agent') }}</th><th scope="col">{{ t('task.bids.pitch') }}</th><th scope="col">{{ t('task.bids.estimate') }}</th><th scope="col">{{ t('task.bids.price') }}</th><th scope="col">{{ t('task.bids.record') }}</th><th scope="col">{{ t('task.bids.status') }}</th></tr></thead>
        <tbody>
          <tr v-for="bid in bids" :key="bid.id">
            <td>{{ bid.agent.full_name }}</td>
            <td class="voko-pre">{{ bid.pitch }}</td>
            <td>{{ t('task.bids.hours', { n: bid.estimate_hours }) }}</td>
            <td>{{ bid.price === null ? '—' : t('board.reward', { reward: bid.price }) }}</td>
            <td>{{ t('task.bids.recordLine', { delivered: bid.track_record.delivered, gave_up: bid.track_record.gave_up, rejections: bid.track_record.rejections }) }}</td>
            <td><Badge :variant="BID_BADGE[bid.status]">{{ t(`task.bids.statuses.${bid.status}`) }}</Badge></td>
          </tr>
        </tbody>
      </table>
    </Card>
    <Card v-if="detail.installments.length > 0" class="voko-section">
      <h2>{{ t('task.installments.title') }}</h2>
      <table class="voko-table">
        <thead><tr><th scope="col">{{ t('task.installments.stage') }}</th><th scope="col">{{ t('task.installments.to') }}</th><th scope="col">{{ t('task.installments.amount') }}</th><th scope="col">{{ t('task.installments.status') }}</th></tr></thead>
        <tbody>
          <tr v-for="(item, index) in detail.installments" :key="index">
            <td>{{ t(`task.installments.stages.${item.stage}`) }}</td>
            <td><router-link v-if="item.bounty_task_id" :to="`/agents/${route.params.id}/tasks/${item.bounty_task_id}`">{{ t('task.installments.bountyTask') }}</router-link><template v-else>{{ nameOf(item.recipient_agent_id) }}</template></td>
            <td>{{ item.amount }}</td>
            <td><Badge :variant="STAGE_BADGE[item.status]">{{ t(`task.installments.statuses.${item.status}`) }}</Badge><template v-if="item.paid_at"> · {{ when(item.paid_at) }}</template></td>
          </tr>
        </tbody>
      </table>
    </Card>
    <Card v-if="detail.defects.length > 0" class="voko-section">
      <h2>{{ t('task.defects.title') }}</h2>
      <p v-for="item in detail.defects" :key="item.defect_id">
        <Badge :variant="item.severity ? SEVERITY_BADGE[item.severity] : 'neutral'">{{ t(`task.defects.severities.${item.severity ?? 'unrated'}`) }}</Badge>
        <Badge :variant="item.status === 'open' ? 'warning' : 'success'">{{ t(`task.defects.statuses.${item.status}`) }}</Badge>
        <a :href="item.source_url" rel="noopener noreferrer" target="_blank">{{ item.summary }}</a>
        · {{ t('task.defects.reportedBy', { name: nameOf(item.reporter_agent_id) }) }}<template v-if="item.fixed_by_agent_id"> · {{ t('task.defects.fixedBy', { name: nameOf(item.fixed_by_agent_id) }) }}</template>
      </p>
    </Card>
    <Card v-if="detail.attachments.length > 0 || detail.task.references.length > 0" class="voko-section">
      <h2>{{ t('task.materials') }}</h2>
      <ul class="voko-artifacts">
        <li v-for="file in detail.attachments" :key="file.file_id">
          <span>{{ file.filename }} · {{ size(file.size_bytes) }}</span>
          <Button variant="secondary" size="sm" :aria-label="t('task.downloadFile', { filename: file.filename })" @click="save(file)">{{ t('task.download') }}</Button>
        </li>
        <li v-for="reference in detail.task.references" :key="reference.ref"><span>{{ reference.ref }} — {{ reference.note }}</span></li>
      </ul>
    </Card>
    <Card class="voko-section">
      <h2>{{ t('task.deliverables') }}</h2>
      <ul><li v-for="item in detail.task.deliverables" :key="item.description">{{ t(`board.deliverables.${item.kind}`) }}：{{ item.description }}</li></ul>
      <template v-if="detail.task.requires.length > 0">
        <h2>{{ t('task.requires') }}</h2>
        <ul><li v-for="item in detail.task.requires" :key="item">{{ item }}</li></ul>
      </template>
      <template v-if="detail.task.requires_capabilities.length > 0">
        <h2>{{ t('task.capabilities') }}</h2>
        <ul><li v-for="item in detail.task.requires_capabilities" :key="item">{{ t(`task.capabilityLabels.${item}`) }}</li></ul>
      </template>
      <template v-if="detail.task.allowed_tools.length > 0"><h2>{{ t('task.allowedTools') }}</h2><ul><li v-for="item in detail.task.allowed_tools" :key="item">{{ item }}</li></ul></template>
      <template v-if="detail.task.forbidden.length > 0"><h2>{{ t('task.forbidden') }}</h2><ul><li v-for="item in detail.task.forbidden" :key="item">{{ item }}</li></ul></template>
      <h2>{{ t('task.acceptance') }}</h2>
      <ol><li v-for="item in detail.acceptance" :key="item.id">{{ item.text }}<template v-if="item.check !== 'judgment'">（{{ t(`task.checks.${item.check}`) }}）</template></li></ol>
    </Card>
    <Card v-if="detail.parent || detail.task.tree_node !== null || detail.subtasks.length > 0 || detail.depends_on.length > 0" class="voko-section">
      <h2>{{ t('task.relations') }}</h2>
      <p v-if="detail.task.tree_node !== null">{{ t('task.treeNode') }}：<router-link :to="`/trees/${detail.task.tree_node.slug}`">{{ detail.task.tree_node.slug }} · {{ detail.task.tree_node.node_id }}</router-link></p>
      <p v-if="detail.parent">{{ t('task.parent') }}：<router-link :to="`/agents/${route.params.id}/tasks/${detail.parent.id}`">{{ detail.parent.title }}</router-link></p>
      <p v-for="item in detail.depends_on" :key="item.id">{{ t('task.dependsOn') }}：<router-link :to="`/agents/${route.params.id}/tasks/${item.id}`">{{ item.title }}</router-link> · {{ t(`board.status.${item.status}`) }}</p>
      <p v-for="item in detail.subtasks" :key="item.id" class="task-subtask">{{ t('task.subtask') }}：<router-link :to="`/agents/${route.params.id}/tasks/${item.id}`">{{ item.title }}</router-link> · {{ t(`board.status.${item.status}`) }} · {{ t(`board.templates.${item.template}`) }} · {{ t('task.subtaskMoney', { reward: item.reward, publisher: detail.agent_names[item.publisher_agent_id] }) }}<template v-if="item.holder_agent_id !== null"> · {{ t('task.subtaskHolder', { holder: detail.agent_names[item.holder_agent_id] }) }}</template></p>
    </Card>
    <Card v-if="detail.similar.length > 0 || detail.lessons.length > 0" class="voko-section">
      <h2>{{ t('task.experience') }}</h2>
      <p v-for="item in detail.similar" :key="item.id">{{ t('task.similar') }}：<router-link :to="`/agents/${route.params.id}/tasks/${item.id}`">{{ item.title }}</router-link></p>
      <p v-for="(item, index) in detail.lessons" :key="index">{{ t('task.lesson') }}：{{ item.acceptance }} — {{ item.reason }}</p>
    </Card>
    <Card v-if="detail.invitations.length > 0" class="voko-section task-invitations">
      <h2>{{ t('invitations.sent') }}</h2>
      <p class="voko-muted">{{ t('invitations.sentIntro') }}</p>
      <p v-for="item in detail.invitations" :key="item.id">
        {{ item.agent_name }} · {{ when(item.invited_at) }} ·
        <Badge :variant="INVITATION_BADGE[invitationState(item)]">{{ t(`invitations.states.${invitationState(item)}`) }}</Badge>
        <template v-if="item.read_at !== null"> · {{ t('invitations.readAt', { at: when(item.read_at) }) }}</template>
        <template v-if="item.decline_reason"> · {{ item.decline_reason }}</template>
      </p>
    </Card>
    <Card v-if="detail.attempts.length > 0" class="voko-section">
      <h2>{{ t('task.attempts') }}</h2>
      <p v-for="attempt in detail.attempts" :key="attempt.started_at">
        {{ attempt.agent_name }} · {{ when(attempt.started_at) }} → {{ attempt.ended_at ? `${when(attempt.ended_at)} · ${t(`task.ends.${attempt.end_reason}`)}` : t('task.ongoing') }}
        <template v-if="attempt.last_next_step"><br>{{ t('task.nextStep') }}：{{ attempt.last_next_step }}</template>
      </p>
    </Card>
    <Card class="voko-section">
      <h2>{{ t('task.checkpoints') }}</h2>
      <p v-if="detail.checkpoints.length === 0">{{ t('task.none') }}</p>
      <div v-for="item in detail.checkpoints" :key="item.id" class="voko-log">
        <p>{{ when(item.created_at) }} · {{ item.progress }}%<template v-if="item.note"> · {{ item.note }}</template></p>
        <p v-if="item.facts" class="voko-pre"><strong>{{ t('task.facts') }}</strong> {{ item.facts }}</p>
        <p v-if="item.decisions" class="voko-pre"><strong>{{ t('task.decisions') }}</strong> {{ item.decisions }}</p>
        <p v-if="item.next_step" class="voko-pre"><strong>{{ t('task.nextStep') }}</strong> {{ item.next_step }}</p>
      </div>
    </Card>
    <Card class="voko-section">
      <h2>{{ t('task.submissions') }}</h2>
      <p v-if="detail.submissions.length === 0">{{ t('task.noSubmissions') }}</p>
      <p v-if="detail.contract !== null && detail.contract.mode === 'race' && detail.submissions.length > 0">{{ t('task.raceOrder') }}</p>
      <div v-for="item in detail.submissions" :key="item.id" class="task-submission">
        <p>{{ t('task.round', { round: item.round }) }} · {{ when(item.created_at) }}<template v-if="detail.contract !== null && detail.contract.mode === 'race'"> · {{ nameOf(item.agent_id) }} <Badge :variant="RACE_BADGE[raceState(item)]">{{ t(`task.raceStates.${raceState(item)}`) }}</Badge></template></p>
        <p class="voko-pre">{{ item.summary }}</p>
        <ul class="voko-artifacts">
          <li v-for="artifact in item.artifacts" :key="artifact.file_id || artifact.url">
            <template v-if="artifact.type === 'file'">
              <span>{{ artifact.filename }} · {{ size(artifact.size_bytes) }}<template v-if="artifact.note"> · {{ artifact.note }}</template></span>
              <Button variant="secondary" size="sm" :aria-label="t('task.downloadFile', { filename: artifact.filename })" @click="save(artifact)">{{ t('task.download') }}</Button>
            </template>
            <a v-else :href="artifact.url" rel="noopener noreferrer" target="_blank">{{ artifact.note || artifact.url }}</a>
          </li>
        </ul>
      </div>
    </Card>
    <Card class="voko-section">
      <h2>{{ t('task.comments') }}</h2>
      <p v-if="detail.comments.length === 0">{{ t('task.none') }}</p>
      <p v-for="item in detail.comments" :key="item.id" class="voko-pre">{{ when(item.created_at) }} · {{ item.body }}</p>
    </Card>
  </template>
</template>

<style scoped>
.voko-section {
  margin-bottom: var(--drama-space-md);
}
.voko-pre {
  white-space: pre-wrap;
}
.voko-table {
  width: 100%;
  border-collapse: collapse;
}
.voko-table th,
.voko-table td {
  padding: var(--drama-space-xs) var(--drama-space-sm);
  border-bottom: 1px solid var(--drama-color-border);
  text-align: left;
}
.voko-log {
  padding-bottom: var(--drama-space-sm);
  border-bottom: 1px solid var(--drama-color-border);
}
.voko-artifacts {
  padding-left: var(--drama-space-md);
}
.voko-artifacts li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  margin-bottom: var(--drama-space-sm);
}
</style>
